import React, { useMemo } from "react";
import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

const normalize = (value) => String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
const money = (value) => `₹${Number.isFinite(Number(value)) ? Number(value).toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "—"}`;
// Keep dashboard totals finite even when legacy data contains extreme numeric values.
const safeAdd = (current, increment) => {
  const total = current + increment;
  return Number.isFinite(total) ? total : current;
};
const isManual = (order) => {
  const source = normalize(order?.source || order?.order_source || "online");
  return ["social_media", "phone", "walk_in", "whatsapp", "manual", "manual_order", "instagram", "facebook"].includes(source)
    || order?.product_slug === "manual-custom-order"
    || (Array.isArray(order?.items) ? order.items : []).some((item) => item?.product_slug === "manual-custom-order");
};
const isCod = (order) => ["cod", "cash_on_delivery"].includes(normalize(order?.payment_method));

export default function AdminDashboardSalesPerformance({ orders = [] }) {
  const { trend, mix, totals } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(today);
      date.setDate(today.getDate() - (6 - index));
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      return { key, label: date.toLocaleDateString("en-IN", { day: "numeric", month: "short" }), revenue: 0, paid: 0, orders: 0 };
    });
    const byDate = new Map(days.map((day) => [day.key, day]));
    const accepted = orders.filter((order) => {
      if (["cancelled", "canceled"].includes(normalize(order?.status))) return false;
      const payment = normalize(order?.payment_status || "pending");
      return isManual(order) || isCod(order) || payment === "paid";
    });
    let paidRevenue = 0;
    let pendingRevenue = 0;
    let recognizedRevenue = 0;
    const productMix = { apparel: 0, gifts: 0, other: 0 };
    accepted.forEach((order) => {
      // Match the canonical amount used by dashboard KPIs and Sales Details.
      const amount = Number(order?.total_amount ?? 0);
      // Keep finite negative adjustments consistent with dashboard totals;
      // cancelled orders are excluded above, so these remain visible as net value.
      if (Number.isFinite(amount)) {
        recognizedRevenue = safeAdd(recognizedRevenue, amount);
        if (normalize(order.payment_status) === "paid") paidRevenue = safeAdd(paidRevenue, amount);
        else pendingRevenue = safeAdd(pendingRevenue, amount);
      }
      const created = order?.created_at ? new Date(order.created_at) : null;
      if (created && !Number.isNaN(created.getTime())) {
        const key = `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, "0")}-${String(created.getDate()).padStart(2, "0")}`;
        const day = byDate.get(key);
        if (day) {
          // Count every dated eligible order. Finite amounts, including negative
          // adjustments, contribute to net recorded value; malformed amounts do not.
          day.orders += 1;
          if (Number.isFinite(amount)) {
            day.revenue = safeAdd(day.revenue, amount);
            if (normalize(order.payment_status) === "paid") day.paid = safeAdd(day.paid, amount);
          }
        }
      }
      const items = Array.isArray(order.items) ? order.items : [];
      items.forEach((item) => {
        const rawQuantity = Number(item?.quantity ?? 0);
        const rawUnit = Number(item?.unit_price ?? 0);
        // Malformed legacy item values must not turn all mix percentages into NaN.
        const quantity = Number.isFinite(rawQuantity) ? Math.max(0, rawQuantity) : 0;
        const unit = Number.isFinite(rawUnit) ? Math.max(0, rawUnit) : 0;
        const value = quantity * unit;
        // Even finite inputs can overflow when multiplied; skip non-finite line values.
        if (!Number.isFinite(value)) return;
        const type = normalize(item?.product_type || item?.type);
        if ([
          "gift", "gifts", "mug", "mugs", "personalised_mug", "personalized_mug",
          "tumbler", "tumblers", "keychain", "keychains", "key_ring", "key_rings",
          "photo_pillow", "pillow", "pillows", "acrylic_blank", "photo_tile", "photo_tiles",
          "photo_plate", "photo_plates", "gift_item", "gift_items", "personalised_gifts", "personalized_gifts",
          "custom_gift", "custom_gifts", "gift_box", "gift_boxes", "photo_frame", "photo_frames",
          "acrylic_keychain", "acrylic_keychains", "personalised_tumbler", "personalized_tumbler",
        ].includes(type)) productMix.gifts = safeAdd(productMix.gifts, value);
        else if ([
          "apparel", "clothing", "tshirt", "t_shirt", "tshirts", "t_shirts",
          "shirt", "shirts", "hoodie", "hoodies", "jersey", "jerseys",
          // Manual orders may store the customer-facing product name as type.
          "custom_t_shirt", "custom_t_shirts", "custom_hoodie", "custom_hoodies",
          "oversized_t_shirt", "oversized_t_shirts", "custom_jersey", "custom_jerseys",
        ].includes(type)) productMix.apparel = safeAdd(productMix.apparel, value);
        else productMix.other = safeAdd(productMix.other, value);
      });
    });
    return {
      trend: days,
      mix: [
        { label: "Apparel", amount: productMix.apparel, color: "bg-[#D95328]" },
        { label: "Gifts", amount: productMix.gifts, color: "bg-[#141413]" },
        { label: "Unclassified items", amount: productMix.other, color: "bg-stone-300" },
      ],
      totals: { recognizedRevenue, paidRevenue, pendingRevenue },
    };
  }, [orders]);

  const maxMixAmount = mix.reduce((max, item) => Math.max(max, item.amount), 0);
  // Scale before summing so large but finite values cannot overflow the percentage denominator.
  const scaledMixTotal = maxMixAmount > 0
    ? mix.reduce((sum, item) => sum + item.amount / maxMixAmount, 0)
    : 0;
  const lastSevenDays = trend.reduce((sum, day) => safeAdd(sum, day.revenue), 0);

  return (
    <section className="grid grid-cols-1 gap-4 xl:grid-cols-5" data-testid="admin-sales-performance">
      <div className="card-pmt p-4 sm:p-5 xl:col-span-3">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="overline">Sales performance</div>
            <h2 className="mt-1 text-xl font-bold">Last 7 days</h2>
            <p className="mt-1 text-sm text-stone-600">Net recorded order value by order date, including COD and manual orders.</p>
          </div>
          <div className="text-sm"><span className="text-stone-500">7-day total</span> <strong className="ml-1">{money(lastSevenDays)}</strong></div>
        </div>
        <div className="mt-4 h-64 w-full" data-testid="dashboard-revenue-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#E6DFD5" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(value) => value >= 1000 ? `₹${Math.round(value / 1000)}k` : `₹${value}`} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={46} />
              <Tooltip formatter={(value, name) => [money(value), name === "paid" ? "Paid revenue" : "Net order value"]} labelFormatter={(label) => { const day = trend.find((entry) => entry.label === label); return day ? `${label} · ${day.orders} order${day.orders === 1 ? "" : "s"}` : label; }} labelStyle={{ color: "#141413" }} />
              <Line type="monotone" dataKey="revenue" name="revenue" stroke="#D95328" strokeWidth={3} dot={{ r: 3, fill: "#D95328" }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="paid" name="paid" stroke="#141413" strokeWidth={2} strokeDasharray="5 4" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 flex flex-wrap gap-4 text-xs text-stone-600">
          <span className="inline-flex items-center gap-2"><span className="h-2 w-5 rounded-full bg-[#D95328]" />Net order value</span>
          <span className="inline-flex items-center gap-2"><span className="h-0.5 w-5 bg-[#141413]" />Paid revenue</span>
        </div>
      </div>

      <div className="card-pmt p-4 sm:p-5 xl:col-span-2">
        <div className="overline">Product mix</div>
        <h2 className="mt-1 text-xl font-bold">Apparel vs gifts</h2>
        <p className="mt-1 text-sm text-stone-600">Across currently loaded eligible orders; quantity × unit price, before shipping and order-level discounts.</p>
        <div className="mt-5 space-y-4">
          {mix.map((item) => {
            const percentage = scaledMixTotal > 0 && maxMixAmount > 0
              ? ((item.amount / maxMixAmount) / scaledMixTotal) * 100
              : 0;
            return (
              <div key={item.label}>
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-semibold">{item.label}</span>
                  <span className="shrink-0 tabular-nums">{money(item.amount)} <span className="text-xs text-stone-500">({Math.round(percentage)}%)</span></span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#F3EDE2]">
                  <div className={`h-full rounded-full ${item.color}`} style={{ width: `${percentage}%` }} />
                </div>
              </div>
            );
          })}
          {maxMixAmount === 0 && <p className="text-sm text-stone-500">Product mix will appear when order items include prices and product types.</p>}
        </div>
        <div className="mt-6 border-t border-[#E6DFD5] pt-4">
          <div className="flex items-center justify-between gap-3 text-sm"><span className="text-stone-600">Net recognized order value</span><strong>{money(totals.recognizedRevenue)}</strong></div>
          <div className="mt-2 flex items-center justify-between gap-3 text-sm"><span className="text-stone-600">Marked paid</span><strong className="text-emerald-700">{money(totals.paidRevenue)}</strong></div>
          <div className="mt-2 flex items-center justify-between gap-3 text-sm"><span className="text-stone-600">Not marked paid</span><strong>{money(totals.pendingRevenue)}</strong></div>
        </div>
      </div>
    </section>
  );
}
